import React, {
    PropsWithChildren,
    useState,
    createContext,
    useContext
} from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import ChevronDownIcon from "@atlaskit/icon/glyph/chevron-down";
import { Box, xcss } from "@atlaskit/primitives";
import Tabs, { TabList, useTabPanel, useTab } from "@atlaskit/tabs";

import { ContentBlock } from "@/components/wpl/content-block/src";
import { Link } from "@/components/wpl/link/src";

export type ProductContext = {
    selectedProduct: string;
};

const SelectedProductContext = createContext<ProductContext>({
    selectedProduct: ""
});

export const useSelectedProduct = () => {
    const context = useContext(SelectedProductContext);
    if (!context) {
        throw new Error(
            "useSelectedProduct must be used within a SelectedProductProvider"
        );
    }
    return context;
};

export const SelectedProductProvider = (
    props: PropsWithChildren<ProductContext>
) => {
    console.log("SelectedProductProvider", props.selectedProduct);
    return (
        <SelectedProductContext.Provider
            value={{ selectedProduct: props.selectedProduct }}
        >
            {props.children}
        </SelectedProductContext.Provider>
    );
};

interface ProductSelectorProps {
    onChangeHandler?: (selected: string, platform: string) => void;
}

export const ProductSelector = ({ onChangeHandler }: ProductSelectorProps) => {
    const [isOpen, setIsOpen] = useState(false);

    const toggleOpen = () => {
        setIsOpen(!isOpen);
    };
    const [selectedOption, setSelectedOption] = useState("");
    const intl = useIntl();
    const productSelectorButtonStyle = [
        isOpen &&
            css({
                "borderColor": "transparent",
                "borderRadius": "4px",
                "cursor": "pointer",
                "display": "flex",
                "flexDirection": "row",
                "justifyContent": "space-between",
                "width": "100%",
                "padding": "0",
                "alignItems": "center",
                "> .selected-option": {
                    lineHeight: "2",
                    fontSize: "1rem",
                    paddingInlineStart: "1rem",
                    textDecoration: "bold",
                    fontFamily:
                        "'Charlie Text', -apple-system, 'system-ui', 'Segoe UI', Roboto, 'Noto Sans', Ubuntu, 'Droid Sans', 'Helvetica Neue', sans-serif"
                },
                ".arrow-down-icon": {
                    alignItems: "right",
                    transform: "rotate(180deg)"
                }
            }),
        !isOpen &&
            css({
                "borderColor": "transparent",
                "borderRadius": "4px",
                "cursor": "pointer",
                "display": "flex",
                "flexDirection": "row",
                "justifyContent": "space-between",
                "width": "100%",
                "padding": "0",
                "alignItems": "center",
                "> .selected-option": {
                    lineHeight: "2",
                    fontSize: "1rem",
                    paddingInlineStart: "1rem",
                    textDecoration: "bold",
                    fontFamily:
                        "'Charlie Text', -apple-system, 'system-ui', 'Segoe UI', Roboto, 'Noto Sans', Ubuntu, 'Droid Sans', 'Helvetica Neue', sans-serif"
                },
                ".arrow-down-icon": {
                    alignItems: "right"
                }
            })
    ];

    const productSelectorStyle = [
        isOpen &&
            css({
                "display": "flex",
                "flexDirection": "column",
                "*>[hidden]": {
                    display: "none !important"
                },
                "width": "100%",
                "[role=tabpanel]": {
                    display: "flex",
                    flexDirection: "column"
                },
                "[class*=-Tabs]": {
                    marginInlineStart: ".9rem"
                },
                "[role=tab]": {
                    "fontSize": "1rem",
                    "fontWeight": "400",
                    "padding": "15px 8px",
                    "letterSpacing": "normal",
                    "lineHeight": "normal",
                    "color": "#000000",

                    "alignItems": "flex-start",
                    "boxSizing": "border-box",
                    "display": "block",
                    "fontFeatureSettings": "normal",
                    "fontKerning": "auto",
                    "fontOpticalSizing": "auto",
                    "fontStretch": "100%",
                    "fontStyle": "normal",
                    "textAlign": "center",
                    "textIndent": "0px",
                    "textShadow": "none",
                    "textTransform": "none",
                    "wordSpacing": "0px",

                    "&:hover": {
                        color: "#0052cc"
                    },

                    "&:focus": {
                        borderColor: "#0052cc",
                        cursor: "pointer",
                        borderStyle: "solid",
                        padding: "15px 8px",
                        borderWidth: "1px 1px 2px 1px"
                    }
                },
                ".text-section": {
                    "h5": {
                        padding: "1rem 0",
                        margin: "0",
                        color: "#8993A4",
                        fontFamily: `"Charlie Text", -apple-system, "system-ui", "Segoe UI", Roboto, "Noto Sans", Ubuntu, "Droid Sans", "Helvetica Neue", sans-serif`,
                        fontWeight: 500,
                        textTransform: "uppercase",
                        letterSpacing: "1px",
                        lineHeight: "12px",
                        fontSize: ".75rem"
                    },
                    ".contentBlock-body": {
                        ".link-wrapper": {
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-start",
                            padding: "8px 0 8px",
                            a: {
                                "fontWeight": 400,
                                "margin": "0",
                                "padding": "0",
                                "&:hover": {
                                    color: "#000000",
                                    textShadow: "none",
                                    textDecoration: "underline"
                                }
                            }
                        },
                        "marginBlockEnd": "0px"
                    }
                }
            }),
        !isOpen &&
            css({
                "display": "none",
                "*>[hidden]": {
                    display: "none !important"
                },
                "width": "100%",
                "[role=tabpanel]": {
                    display: "flex",
                    flexDirection: "column"
                },
                ".text-section": {
                    "h5": {
                        padding: "1rem 0",
                        margin: "0",
                        color: "#8993A4",
                        fontFamily: `"Charlie Text", -apple-system, "system-ui", "Segoe UI", Roboto, "Noto Sans", Ubuntu, "Droid Sans", "Helvetica Neue", sans-serif`,
                        fontWeight: 500,
                        textTransform: "uppercase",
                        letterSpacing: "1px",
                        lineHeight: "12px",
                        fontSize: ".75rem"
                    },
                    ".contentBlock-body": {
                        ".link-wrapper": {
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-start",
                            a: {
                                "fontWeight": 400,
                                "margin": "0",
                                "padding": "0",
                                "&:hover": {
                                    color: "#000000",
                                    textShadow: "none",
                                    textDecoration: "underline"
                                }
                            }
                        },
                        "marginBlockEnd": "0px"
                    }
                }
            })
    ];

    const divCss = css({
        fontFamily:
            "'Charlie Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Noto Sans', 'Ubuntu', 'Droid Sans', 'Helvetica Neue', sans-serif",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        width: "100%"
    });

    const onClick =
        (platform: string) => (event: React.MouseEvent<HTMLElement>) => {
            const selected = String(event.currentTarget.dataset["label"]);
            setSelectedOption(selected);
            onChangeHandler?.(selected, platform);
            toggleOpen();
        };

    const servicesText = intl.formatMessage({
        id: "migrationProductSelector.services",
        defaultMessage: "Services"
    });

    const advisoryServicesText = intl.formatMessage({
        id: "migrationProductSelector.advisoryServices",
        defaultMessage: "Advisory Services"
    });

    const premierSupportText = intl.formatMessage({
        id: "migrationProductSelector.premierSupport",
        defaultMessage: "Premier Support"
    });

    const prioritySupportText = intl.formatMessage({
        id: "migrationProductSelector.prioritySupport",
        defaultMessage: "Priority Support"
    });

    const CloudTab = () => {
        const cloudProductsText = intl.formatMessage({
            id: "migrationProductSelector.cloudProducts",
            defaultMessage: "Cloud Products"
        });

        const cloudPlatform = "Cloud";

        const tabPanelAttributes = useTabPanel();
        return (
            <div {...tabPanelAttributes}>
                <ContentBlock
                    headerType={"h5"}
                    orientation={"vertical"}
                    headerText={cloudProductsText}
                    bodyText={
                        <>
                            <Link
                                customLinkColor={"#253858"}
                                text={"Jira"}
                                type={"anchor"}
                                href={"#"}
                                key={"jira-software"}
                                dataEventProps={{
                                    "data-label": "Jira",
                                    "data-product-key": "jira",
                                    "data-event-component": "link",
                                    "data-event-container": "productSelector",
                                    "data-event": "clicked",
                                    "data-label-english": "Jira"
                                }}
                                // @ts-ignore
                                onClick={onClick(cloudPlatform)}
                            />

                            <Link
                                customLinkColor={"#253858"}
                                text={"Confluence"}
                                type={"anchor"}
                                href={"#"}
                                key={"confluence"}
                                dataEventProps={{
                                    "data-label": "Confluence",
                                    "data-product-key": "confluence",
                                    "data-event-component": "link",
                                    "data-event-container": "productSelector",
                                    "data-event": "clicked",
                                    "data-label-english": "Confluence"
                                }}
                                // @ts-ignore
                                onClick={onClick(cloudPlatform)}
                            />

                            <Link
                                customLinkColor={"#253858"}
                                text={"Jira Service Management"}
                                type={"anchor"}
                                href={"#"}
                                key={"jira-service-management"}
                                dataEventProps={{
                                    "data-label": "Jira Service Management",
                                    "data-product-key": "jiraServiceManagement",
                                    "data-event-component": "link",
                                    "data-event-container": "productSelector",
                                    "data-event": "clicked",
                                    "data-label-english":
                                        "Jira Service Management"
                                }}
                                // @ts-ignore
                                onClick={onClick(cloudPlatform)}
                            />

                            <Link
                                customLinkColor={"#253858"}
                                text={"Bitbucket"}
                                type={"anchor"}
                                href={"#"}
                                key={"bitbucket"}
                                dataEventProps={{
                                    "data-label": "Bitbucket",
                                    "data-product-key": "bitbucket",
                                    "data-event-component": "link",
                                    "data-event-container": "productSelector",
                                    "data-event": "clicked",
                                    "data-label-english": "Bitbucket"
                                }}
                                // @ts-ignore
                                onClick={onClick(cloudPlatform)}
                            />

                            <Link
                                customLinkColor={"#253858"}
                                text={"Jira Align"}
                                type={"anchor"}
                                href={"#"}
                                key={"jira-align"}
                                dataEventProps={{
                                    "data-label": "Jira Align",
                                    "data-product-key": "jiraAlign",
                                    "data-event-component": "link",
                                    "data-event-container": "productSelector",
                                    "data-event": "clicked",
                                    "data-label-english": "Jira Align"
                                }}
                                // @ts-ignore
                                onClick={onClick(cloudPlatform)}
                            />

                            <Link
                                customLinkColor={"#253858"}
                                text={"Atlassian Guard"}
                                type={"anchor"}
                                href={"#"}
                                key={"atlassian-access"}
                                dataEventProps={{
                                    "data-label": "Atlassian Guard",
                                    "data-product-key": "atlassianGuard",
                                    "data-event-component": "link",
                                    "data-event-container": "productSelector",
                                    "data-event": "clicked",
                                    "data-label-english": "Atlassian Guard"
                                }}
                                // @ts-ignore
                                onClick={onClick(cloudPlatform)}
                            />

                            <Link
                                customLinkColor={"#253858"}
                                text={"Opsgenie"}
                                type={"anchor"}
                                href={"#"}
                                key={"opsgenie"}
                                dataEventProps={{
                                    "data-label": "Opsgenie",
                                    "data-product-key": "opsgenie",
                                    "data-event-component": "link",
                                    "data-event-container": "productSelector",
                                    "data-event": "clicked",
                                    "data-label-english": "Opsgenie"
                                }}
                                // @ts-ignore
                                onClick={onClick(cloudPlatform)}
                            />
                            <Link
                                customLinkColor={"#253858"}
                                text={"Trello"}
                                type={"anchor"}
                                href={"#"}
                                key={"trello"}
                                dataEventProps={{
                                    "data-label": "Trello",
                                    "data-product-key": "trello",
                                    "data-event-component": "link",
                                    "data-event-container": "productSelector",
                                    "data-event": "clicked",
                                    "data-label-english": "Trello"
                                }}
                                // @ts-ignore
                                onClick={onClick(cloudPlatform)}
                            />
                            <Link
                                customLinkColor={"#253858"}
                                text={"Jira Product Discovery"}
                                type={"anchor"}
                                href={"#"}
                                key={"jira-product-discovery"}
                                dataEventProps={{
                                    "data-label": "Jira Product Discovery",
                                    "data-product-key": "jiraProductDiscovery",
                                    "data-event-component": "link",
                                    "data-event-container": "productSelector",
                                    "data-event": "clicked",
                                    "data-label-english":
                                        "Jira Product Discovery"
                                }}
                                // @ts-ignore
                                onClick={onClick(cloudPlatform)}
                            />
                            <Link
                                customLinkColor={"#253858"}
                                text={"Compass"}
                                type={"anchor"}
                                href={"#"}
                                key={"compass"}
                                dataEventProps={{
                                    "data-label": "Compass",
                                    "data-product-key": "compass",
                                    "data-event-component": "link",
                                    "data-event-container": "productSelector",
                                    "data-event": "clicked",
                                    "data-label-english": "Compass"
                                }}
                                // @ts-ignore
                                onClick={onClick(cloudPlatform)}
                            />
                        </>
                    }
                />

                <ContentBlock
                    headerText={servicesText}
                    headerType={"h5"}
                    orientation={"vertical"}
                    bodyText={
                        <>
                            <Link
                                customLinkColor={"#253858"}
                                text={advisoryServicesText}
                                type={"anchor"}
                                href={"#"}
                                key={"advisoryServices"}
                                dataEventProps={{
                                    "data-label": "Advisory Services",
                                    "data-product-key": "support",
                                    "data-event-component": "link",
                                    "data-event-container": "productSelector",
                                    "data-event": "clicked",
                                    "data-label-english": "Advisory Services"
                                }}
                                // @ts-ignore
                                onClick={onClick(cloudPlatform)}
                            />
                        </>
                    }
                />
                <div css={css({ marginBlockEnd: "20px" })} />
            </div>
        );
    };

    const DCTab = () => {
        const tabPanelAttributes = useTabPanel();
        const dataCenterProductsText = intl.formatMessage({
            id: "migrationProductSelector.dataCenterProducts",
            defaultMessage: "Data Center Products"
        });
        const dcPlatform = "Data Center";
        return (
            <div {...tabPanelAttributes}>
                <ContentBlock
                    headerType={"h5"}
                    headerText={dataCenterProductsText}
                    bodyText={
                        <>
                            <Link
                                customLinkColor={"#253858"}
                                text={"Jira Software"}
                                type={"anchor"}
                                href={"#"}
                                key={"jira-software"}
                                data-deployment-type={"Data Center"}
                                dataEventProps={{
                                    "data-label": "Jira Software",
                                    "data-product-key": "jiraSoftware",
                                    "data-event-component": "link",
                                    "data-event-container": "productSelector",
                                    "data-event": "clicked",
                                    "data-label-english": "Jira Software"
                                }}
                                // @ts-ignore
                                onClick={onClick(dcPlatform)}
                            />

                            <Link
                                customLinkColor={"#253858"}
                                text={"Confluence"}
                                type={"anchor"}
                                href={"#"}
                                key={"confluence"}
                                dataEventProps={{
                                    "data-label": "Confluence",
                                    "data-product-key": "confluence",
                                    "data-event-component": "link",
                                    "data-event-container": "productSelector",
                                    "data-event": "clicked",
                                    "data-label-english": "Confluence"
                                }}
                                // @ts-ignore
                                onClick={onClick(dcPlatform)}
                            />

                            <Link
                                customLinkColor={"#253858"}
                                text={"Jira Service Management"}
                                type={"anchor"}
                                href={"#"}
                                key={"jira-service-management"}
                                data-deployment-type={"Data Center"}
                                dataEventProps={{
                                    "data-label": "Jira Service Management",
                                    "data-product-key": "jiraServiceManagement",
                                    "data-event-component": "link",
                                    "data-event-container": "productSelector",
                                    "data-event": "clicked",
                                    "data-label-english":
                                        "Jira Service Management"
                                }}
                                // @ts-ignore
                                onClick={onClick(dcPlatform)}
                            />

                            <Link
                                customLinkColor={"#253858"}
                                text={"Bitbucket"}
                                type={"anchor"}
                                href={"#"}
                                key={"bitbucket"}
                                dataEventProps={{
                                    "data-label": "Bitbucket",
                                    "data-product-key": "bitbucket",
                                    "data-event-component": "link",
                                    "data-event-container": "productSelector",
                                    "data-event": "clicked",
                                    "data-label-english": "Bitbucket"
                                }}
                                // @ts-ignore
                                onClick={onClick(dcPlatform)}
                            />

                            <Link
                                customLinkColor={"#253858"}
                                text={"Crowd"}
                                type={"anchor"}
                                href={"#"}
                                key={"crowd"}
                                dataEventProps={{
                                    "data-label": "Crowd",
                                    "data-product-key": "crowd",
                                    "data-event-component": "link",
                                    "data-event-container": "productSelector",
                                    "data-event": "clicked",
                                    "data-label-english": "Crowd"
                                }}
                                // @ts-ignore
                                onClick={onClick(dcPlatform)}
                            />
                        </>
                    }
                />
                <ContentBlock
                    headerText={servicesText}
                    headerType={"h5"}
                    bodyText={
                        <>
                            <Link
                                customLinkColor={"#253858"}
                                text={premierSupportText}
                                type={"anchor"}
                                href={"#"}
                                key={"premier-support"}
                                dataEventProps={{
                                    "data-label": "Premier support",
                                    "data-product-key": "support",
                                    "data-event-component": "link",
                                    "data-event-container": "productSelector",
                                    "data-event": "clicked",
                                    "data-label-english": "Premier Support"
                                }}
                                // @ts-ignore
                                onClick={onClick(dcPlatform)}
                            />
                            <Link
                                customLinkColor={"#253858"}
                                text={prioritySupportText}
                                type={"anchor"}
                                href={"#"}
                                key={"priority-support"}
                                dataEventProps={{
                                    "data-label": "Priority support",
                                    "data-product-key": "support",
                                    "data-event-component": "link",
                                    "data-event-container": "productSelector",
                                    "data-event": "clicked",
                                    "data-label-english": "Priority Support"
                                }}
                                // @ts-ignore
                                onClick={onClick(dcPlatform)}
                            />
                            <Link
                                customLinkColor={"#253858"}
                                text={advisoryServicesText}
                                type={"anchor"}
                                href={"#"}
                                key={"advisory-services"}
                                dataEventProps={{
                                    "data-label": "Advisory Services",
                                    "data-product-key": "support",
                                    "data-event-component": "link",
                                    "data-event-container": "productSelector",
                                    "data-event": "clicked",
                                    "data-label-english": "Advisory Services"
                                }}
                                // @ts-ignore
                                onClick={onClick(dcPlatform)}
                            />
                        </>
                    }
                />
                <div css={css({ marginBlockEnd: "20px" })} />
            </div>
        );
    };

    const customTabStyles = xcss({
        alignItems: "flex-start",
        boxSizing: "border-box",
        display: "block",
        fontFamily: `"Charlie Text", -apple-system, "system-ui", "Segoe UI", Roboto, "Noto Sans", Ubuntu, "Droid Sans", "Helvetica Neue", sans-serif`,
        fontFeatureSettings: "normal",
        fontKerning: "auto",
        fontOpticalSizing: "auto",
        fontSize: "16px",
        fontStretch: "100%",
        fontStyle: "normal",
        fontVariationSettings: "normal",
        fontWeight: "400",
        letterSpacing: "normal",
        lineHeight: "normal",
        textAlign: "center",
        textIndent: "0px",
        textShadow: "none",
        textTransform: "none",
        wordSpacing: "0px"
    });

    const CustomTab = ({ label }: { label: string }) => {
        const tabAttributes = useTab();

        return (
            <Box xcss={customTabStyles} {...tabAttributes}>
                {label}
            </Box>
        );
    };

    const tabs = (
        <Tabs id={"product-selector"}>
            <TabList>
                <CustomTab label="Cloud" />
                <CustomTab label="Data Center" />
            </TabList>
            <CloudTab />
            <DCTab />
        </Tabs>
    );

    return (
        <div className={"product-selector"} css={divCss}>
            <button
                type={"button"}
                css={productSelectorButtonStyle}
                onClick={toggleOpen}
            >
                <span className={"selected-option"}>{selectedOption}</span>
                <span className={"arrow-down-icon"}>
                    <ChevronDownIcon
                        label={isOpen ? "close menu" : "open menu"}
                    />
                </span>
            </button>
            <div css={productSelectorStyle}>{tabs}</div>
        </div>
    );
};
